import { Injectable } from '@angular/core';
import { App } from '@df/core/app';
import { ISizeName } from '@df/core/service/client';
import get from 'lodash-es/get';
import { ICmsModel } from '@icms/core/resource/model/icms-model';
import transform from 'lodash-es/transform';

export namespace NICmsDataScope {
    export interface IMapEntry<T> {
        source: T;
        model: ICmsModel;
        path: (string | number)[];
        size: ISizeName;
    }
}

declare module '@df/core/app' {
    interface App {
        iCmsDataScopeService: ICmsDataScopeService;
    }
}

@Injectable({
    providedIn: 'root'
})
export class ICmsDataScopeService {
    /**
     * Map
     * scope data object => source information
     */
    readonly objectMap = new Map<any, NICmsDataScope.IMapEntry<any>>();

    constructor(app: App) {
        app.iCmsDataScopeService = this;
        // flush model cache when data is reset
        // DON'T, it casuses too many problems
        // iCmsDataChangeService.beforeChange$.subscribe(change => {
        //     if (
        //         change.model.hasScopeData &&
        //         change.changeType === NICmsDataChange.EChangeType.change &&
        //         (change as NICmsDataChange.IEventChange).previousValue &&
        //         change.flag === 'dataInit'
        //     ) {
        //         this.flushByModel(change.model);
        //     }
        // });
    }

    /**
     * Deep object transformation mapping per-size data keys into final ones
     */
    transform<T>(source: T, sizeName: ISizeName, model: ICmsModel, path: (string | number)[]): T {
        if (!model.hasScopeData) {
            console.error('Model without scope data is transformed', model);
        }
        if (typeof source !== 'object') {
            return source;
        }
        const transformObject = (source, path: (string | number)[]) => {
            if (source && typeof source === 'object') {
                const existingResult = this.getScopeDataBySource(source, sizeName);
                if (existingResult) {
                    return existingResult;
                }

                const result = Array.isArray(source)
                    ? source.map((item, index) => transformObject(item, [...path, index]))
                    : transform(source, (target: any, value, key) => {
                          if (typeof key === 'string' && key.includes('@')) {
                              const parts = key.split('@');
                              if (parts[1] === sizeName) {
                                  target[parts[0]] = transformObject(value, [...path, parts[0] as string | number]);
                              }
                          } else if (!(key in target)) {
                              target[key] = transformObject(value, [...path, key as string | number]);
                          }
                      });

                this.objectMap.set(result, {
                    source: source,
                    model,
                    path,
                    size: sizeName
                });
                return result;
            }
            return source;
        };
        return transformObject(source, path);
    }

    /**
     * Retrieves single scope data for given source object and size
     * @param source Source object from which data was created
     * @param sizeName size name
     * @returns Scope data created
     */
    private getScopeDataBySource<T>(source: T, sizeName: ISizeName): T | undefined {
        for (const [scopeData, entry] of this.objectMap) {
            if (entry.source === source && entry.size === sizeName) {
                return scopeData;
            }
        }
    }

    /**
     * Flushes map entries assigned to a model
     */
    //    private flushByModel(model: Model) {
    //         console.warn('flush by model', model);
    //         this.objectMap.forEach((entry, scopeData) => {
    //             if (entry.model === model) {
    //                 this.objectMap.delete(scopeData);
    //             }
    //         });
    //     }

    /**
     * Get current object
     * Replaces object with one that is currently
     */
    getCurrent<T>(data: T): T {
        const entry = this.objectMap.get(data)!;
        return entry.path.length ? (get(entry.model, entry.path) as T) : (entry.model as T);
    }
}
